import * as ss from 'superstruct';
import { ObjectSchema, ObjectType } from 'superstruct/dist/utils';
import { InvalidParamError } from './invalid-param-error';

/** Is null or undefined. */
export const IsEmpty = '__IsEmpty__' as const;

export type IsEmpty = typeof IsEmpty;

/** Is defined. */
export const IsDefined = '__IsDefined__' as const;

export type IsDefined = typeof IsDefined;

type FilterParamsExact<T> = {
  [K in keyof T]?: T[K] | T[K][] | typeof IsEmpty | typeof IsDefined;
};

type FilterParamsQ = {
  q?: string;
};

const RangeComparator = ss.enums(['lt', 'lte', 'gt', 'gte', 'neq']);

export type RangeComparator = ss.Infer<typeof RangeComparator>;

type FilterParamsRange<T> = {
  [K in keyof T as `${K extends string ? K : never}_${RangeComparator}`]?: T[K];
};

type FilterParamsRangeDecomposed<T> = {
  [K in keyof T]?: { comparator: RangeComparator; value: T[K] }[];
};

export type FilterParams<T> = FilterParamsExact<T> &
  FilterParamsQ &
  FilterParamsRange<T>;

export type FilterParamsDecomposed<T> = {
  exact?: FilterParamsExact<T>;
  range?: FilterParamsRangeDecomposed<T>;
} & FilterParamsQ;

const filterParamSchema = <T extends ObjectType<ObjectSchema>>(
  struct: ss.Struct<T, ObjectSchema>,
) => {
  const schema = struct.schema;

  let fullCodec: ss.Struct<ObjectType<ObjectSchema>, ObjectSchema> = ss.object(
    {},
  );

  // Add q key.
  fullCodec = ss.assign(fullCodec, ss.object({ q: ss.string() }));

  // Add exact and range keys.
  const fields = Object.keys(schema);

  for (const o of fields) {
    // Exact can be either scalar or array.
    const valueCodec = ss.union([
      schema[o],
      ss.array(schema[o]),
      ss.literal(IsDefined),
      ss.literal(IsEmpty),
    ]);

    fullCodec = ss.assign(fullCodec, ss.object({ [o]: valueCodec })); // Exact.

    for (const ext of Object.keys(RangeComparator.schema)) {
      fullCodec = ss.assign(
        fullCodec,
        ss.object({ [`${o}_${ext}`]: schema[o] }),
      ); // Range.
    }
  }

  return ss.partial(fullCodec);
};

export const isValidFilterParam = <T extends ObjectType<ObjectSchema>>(
  filterParam: unknown,
  struct: ss.Struct<T, ObjectSchema>,
): filterParam is FilterParams<T> => {
  const filterCodec = filterParamSchema(struct);

  return ss.is(filterParam, filterCodec);
};

/**
 * Convert a FilterParams object into a more usable form - with the different
 * forms of filters broken out into different, typed objects.
 */
export const decomposeFilterParam = <T extends ObjectType<ObjectSchema>>(
  params: FilterParams<T>,
  struct: { schema: ObjectSchema },
): FilterParamsDecomposed<T> => {
  const fields = new Set<keyof T>(Object.keys(struct.schema));
  const ret: FilterParamsDecomposed<T> = {};

  const isField = (param: string): param is string & keyof T =>
    fields.has(param);

  for (const p of Object.keys(params)) {
    if (fields.has(p)) {
      // Exact.
      if (!ret.exact) {
        ret.exact = {};
      }

      // @ts-expect-error - we know that p is a key of T.
      ret.exact[p] = params[p];
    } else if (p === 'q') {
      // Q.
      ret.q = params.q;
    } else {
      // Range.
      const [field, rangeRaw] = p.split('_', 2);
      const [error, comparator] = ss.validate(rangeRaw, RangeComparator);

      if (!isField(field) || error) {
        throw new InvalidParamError('Invalid filter param: ' + p);
      }

      if (!ret.range) {
        ret.range = {};
      }

      // TODO: Improve typing here.
      const value: any = params[p];

      const rangeFilter = {
        comparator,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        value,
      } as const;

      if (!ret.range[field]) {
        // @ts-expect-error - we know that field is a key of T.
        ret.range[field] = [rangeFilter];
      } else {
        ret.range[field]?.push(rangeFilter);
      }
    }
  }

  return ret;
};
